import React from 'react'
import { Avatar } from '@/shared/ui'
import { BottomDrawer } from '@/shared/ui/BottomDrawer'
import { SmilePlus } from 'lucide-react'
import {
  computeEmojiPickerPlacement,
  type EmojiPickerAlign,
  type EmojiPickerPlacement,
} from '@/shared/ui/emojiPickerPlacement'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { CommunityCardReactionsRow } from '@/modules/comunidade/components/CommunityCardReactionsRow'
import { EmojiPicker } from '@/modules/comunidade/components/EmojiPicker'
import { CommunityDrawerBase } from '@/modules/comunidade/components/CommunityDrawerBase'
import { CommunityThreadNotificationsToggle } from '@/modules/comunidade/components/CommunityThreadNotificationsToggle'
import { useCommunityThreadPresenceReporter } from '@/modules/comunidade/hooks/useCommunityThreadPresenceReporter'
import { useCommunityModeratorEnabled } from '@/modules/comunidade/hooks/useCommunityModeratorEnabled'
import { isPwaInstalled } from '@/modules/pwa/services/webpushClient'
import type { VisionCommunitySummary } from '../services/visionCommunityService'
import type { VisionPostAttachment } from '../services/visionPostAttachmentsService'
import { useVisionCommunityThread } from '../hooks/useVisionCommunityThread'
import { useVisionCardReactions } from '../hooks/useVisionCardReactions'
import { PostPreviewCard } from '@/modules/post-shared'

type VisionCommunityEntryProps = {
  visionId: number
  threadId?: number | null
  title: string
  contentHtml: string
  attachments: VisionPostAttachment[]
  enabled: boolean
  summary?: VisionCommunitySummary | null
  focusMessageId?: number | null
  footerRight?: React.ReactNode
  overlayContent?: React.ReactNode
}

export function VisionCommunityEntry({
  visionId,
  threadId,
  title,
  contentHtml,
  attachments,
  enabled,
  summary,
  focusMessageId,
  footerRight,
  overlayContent,
}: VisionCommunityEntryProps) {
  const { user, profile } = useAuth()
  const isMaster = profile?.role === 'master'
  const currentUserId = user?.id ? String(user.id) : null
  const moderatorEnabled = useCommunityModeratorEnabled(currentUserId, enabled && !isMaster)
  const isModerator = isMaster || !!moderatorEnabled
  const [open, setOpen] = React.useState(false)
  const lastAutoOpenKeyRef = React.useRef<string | null>(null)
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const [isVisible, setIsVisible] = React.useState(false)
  const [maxCardReactions, setMaxCardReactions] = React.useState(8)
  const [messagesCountOverride, setMessagesCountOverride] = React.useState<number | null>(null)
  const [cardEmojiPickerOpen, setCardEmojiPickerOpen] = React.useState(false)
  const cardEmojiButtonRef = React.useRef<HTMLButtonElement | null>(null)
  const [cardEmojiPickerPlacement, setCardEmojiPickerPlacement] =
    React.useState<EmojiPickerPlacement>('top')
  const [cardEmojiPickerAlign, setCardEmojiPickerAlign] =
    React.useState<EmojiPickerAlign>('left')
  const isLg =
    typeof window === 'undefined' || typeof window.matchMedia !== 'function'
      ? true
      : window.matchMedia('(min-width: 1024px)').matches

  React.useEffect(() => {
    if (isVisible) return

    const el = containerRef.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true)
            observer.disconnect()
          }
        })
      },
      { rootMargin: '200px 0px 0px 0px' },
    )

    observer.observe(el)

    return () => observer.disconnect()
  }, [isVisible])

  React.useEffect(() => {
    if (typeof window === 'undefined') return

    const updateMax = () => {
      const width = window.innerWidth || document.documentElement.clientWidth || 0

      let pwa = false
      try {
        pwa = isPwaInstalled()
      } catch {
        pwa = false
      }

      const isPwaLike = pwa || width <= 768
      setMaxCardReactions(isPwaLike ? 7 : 22)
    }

    updateMax()
    window.addEventListener('resize', updateMax)
    return () => window.removeEventListener('resize', updateMax)
  }, [])

  if (!enabled) return null

  const currentUserName = profile?.full_name || user?.email || 'Usuario'
  const currentUserAvatar = (profile as any)?.avatar_url ?? null

  const effectiveFocusMessageId =
    focusMessageId != null && Number.isFinite(Number(focusMessageId)) && Number(focusMessageId) > 0
      ? Math.floor(Number(focusMessageId))
      : null

  const effectiveThreadId =
    threadId != null && Number.isFinite(Number(threadId)) && Number(threadId) > 0
      ? Math.floor(Number(threadId))
      : visionId

  React.useEffect(() => {
    if (!effectiveFocusMessageId) return
    const key = `${visionId}:${effectiveFocusMessageId}`
    if (lastAutoOpenKeyRef.current === key) return
    lastAutoOpenKeyRef.current = key
    setOpen(true)
  }, [visionId, effectiveFocusMessageId])

  const threadHook = useVisionCommunityThread(
    effectiveThreadId,
    { name: currentUserName, avatarUrl: currentUserAvatar },
    enabled && (isVisible || !!effectiveFocusMessageId) && open,
  )

  const cardReactionsHook = useVisionCardReactions(effectiveThreadId, enabled && isVisible)

  React.useEffect(() => {
    const currentCount = Array.isArray(threadHook.thread?.messages)
      ? threadHook.thread.messages.length
      : 0

    if (currentCount > 0 && messagesCountOverride !== currentCount) {
      setMessagesCountOverride(currentCount)
    }
  }, [threadHook.thread, messagesCountOverride])

  function handleOpen(e: React.MouseEvent) {
    e.stopPropagation()
    setOpen(true)
  }

  const primaryAvatar = summary?.avatars?.[0]
  const secondaryAvatars = summary && summary.avatars.length > 1 ? summary.avatars.slice(1, 5) : []

  const extraCount = summary && summary.avatars.length > 5 ? summary.avatars.length - 5 : 0

  const baseMessagesCount = summary?.messagesCount ?? 0
  const effectiveMessagesCount =
    messagesCountOverride !== null && messagesCountOverride > baseMessagesCount
      ? messagesCountOverride
      : baseMessagesCount

  const responsesText =
    effectiveMessagesCount > 0
      ? `${effectiveMessagesCount} resposta${effectiveMessagesCount > 1 ? 's' : ''}`
      : 'Iniciar conversa'

  const lastLabel = formatLastInteractionLabel(summary?.lastMessageAt)

  const summaryRow = (
    <div className="flex w-full items-start gap-2 text-xs text-foreground/80">
      <div className="flex min-w-0 flex-1 items-start gap-2">
        {primaryAvatar ? (
          <div className="flex items-center gap-1">
            <Avatar
              src={primaryAvatar.authorAvatarUrl || undefined}
              alt={primaryAvatar.authorName || 'Usuario'}
              className="h-6 w-6 border-none"
              fallback={primaryAvatar.authorName?.[0] || 'U'}
            />
            {secondaryAvatars.map((av) => (
              <Avatar
                key={av.authorId}
                src={av.authorAvatarUrl || undefined}
                alt={av.authorName || 'Usuario'}
                className="h-5 w-5 -ml-2 border-none"
                fallback={av.authorName?.[0] || 'U'}
              />
            ))}
            {extraCount > 0 && (
              <div className="-ml-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px]">
                +{extraCount}
              </div>
            )}
          </div>
        ) : (
          <div className="h-6 w-6 rounded-full bg-muted" />
        )}

        <div className="min-w-0 flex flex-col leading-tight">
          <button
            type="button"
            onClick={handleOpen}
            className="text-left text-[11px] font-semibold text-sky-400 hover:underline"
          >
            {responsesText}
          </button>

          {lastLabel ? <span className="text-[10px] text-[#575757]">{lastLabel}</span> : null}
        </div>
      </div>

      {footerRight ? <div className="flex flex-none items-start justify-end">{footerRight}</div> : null}
    </div>
  )

  return (
    <>
      <div ref={containerRef} className="mt-3 text-xs text-foreground/80">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <CommunityCardReactionsRow
              reactions={cardReactionsHook.reactions}
              onToggleEmoji={(emoji) => cardReactionsHook.toggleEmoji(emoji)}
              maxVisibleReactions={maxCardReactions}
            />
            <div className="relative inline-flex">
              <button
                type="button"
                ref={cardEmojiButtonRef}
                onClick={(e) => {
                  e.stopPropagation()
                  try {
                    if (typeof window !== 'undefined' && cardEmojiButtonRef.current) {
                      const buttonRect = cardEmojiButtonRef.current.getBoundingClientRect()
                      const { placement, align } = computeEmojiPickerPlacement({
                        buttonRect,
                        containerRect: null,
                        estimatedPickerHeight: 520,
                        pickerWidth: 320,
                      })
                      setCardEmojiPickerPlacement(placement)
                      setCardEmojiPickerAlign(align)
                    }
                  } catch {
                    // ignore
                  }
                  setCardEmojiPickerOpen((prev) => !prev)
                }}
                className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#1f1f1f] text-[#b3b3b3] hover:bg-[#262626] hover:text-[#e5e5e5]"
              >
                <SmilePlus className="h-4 w-4" />
              </button>
              {cardEmojiPickerOpen && isLg && (
                <div
                  className={[
                    'absolute z-[120]',
                    cardEmojiPickerPlacement === 'top'
                      ? 'bottom-[calc(100%+0.5rem)]'
                      : 'top-7',
                    cardEmojiPickerAlign === 'right' ? 'right-0' : 'left-0',
                  ].join(' ')}
                  onClick={(event) => event.stopPropagation()}
                >
                  <EmojiPicker
                    onSelect={async (emoji) => {
                      await cardReactionsHook.toggleEmoji(emoji)
                      setCardEmojiPickerOpen(false)
                    }}
                    onClose={() => setCardEmojiPickerOpen(false)}
                    canDeleteCustomEmojis={!!isMaster}
                  />
                </div>
              )}
              {!isLg && (
                <BottomDrawer
                  open={cardEmojiPickerOpen}
                  onOpenChange={setCardEmojiPickerOpen}
                  header="Emojis"
                  className="h-[50vh] max-h-[50vh] bg-[#111111]"
                  zIndexClass="z-[180000]"
                  overlayClassName="bg-black/60 backdrop-blur-sm"
                >
                  <EmojiPicker
                    className="w-full"
                    onSelect={async (emoji) => {
                      await cardReactionsHook.toggleEmoji(emoji)
                      setCardEmojiPickerOpen(false)
                    }}
                    onClose={() => setCardEmojiPickerOpen(false)}
                    canDeleteCustomEmojis={!!isMaster}
                  />
                </BottomDrawer>
              )}
            </div>
          </div>

          {summaryRow}
        </div>
      </div>

      <VisionCommunityDrawer
        open={open}
        onOpenChange={setOpen}
        visionId={visionId}
        threadId={effectiveThreadId}
        title={title}
        contentHtml={contentHtml}
        attachments={attachments}
        threadHook={threadHook}
        currentUserId={currentUserId}
        isMaster={!!isMaster}
        isModerator={!!isModerator}
        focusMessageId={effectiveFocusMessageId}
        overlayContent={overlayContent}
      />
    </>
  )
}

type DrawerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  visionId: number
  threadId: number
  title: string
  contentHtml: string
  attachments: VisionPostAttachment[]
  threadHook: ReturnType<typeof useVisionCommunityThread>
  currentUserId: string | null
  isMaster: boolean
  isModerator: boolean
  focusMessageId?: number | null
  overlayContent?: React.ReactNode
}

function VisionCommunityDrawer({
  open,
  onOpenChange,
  visionId,
  threadId,
  title,
  contentHtml,
  attachments,
  threadHook,
  currentUserId,
  isMaster,
  isModerator,
  focusMessageId,
  overlayContent,
}: DrawerProps) {
  const header = (
    <div className="flex flex-col leading-tight">
      <span className="text-sm font-semibold">Conversa - Visoes</span>
      <span className="text-[11px] text-foreground/70 line-clamp-2">{title}</span>
    </div>
  )

  const headerRight = (
    <CommunityThreadNotificationsToggle contextType="vision" contextId={threadId} active={open} />
  )

  useCommunityThreadPresenceReporter({
    active: open,
    contextType: 'vision',
    contextId: threadId,
  })

  const overlay =
    overlayContent ??
    (
      <PostPreviewCard
        title={title}
        contentHtml={contentHtml}
        attachments={attachments}
        lightboxVariant="drawer"
      />
    )

  return (
    <CommunityDrawerBase
      open={open}
      onOpenChange={onOpenChange}
      header={header}
      headerRight={headerRight}
      threadHook={threadHook}
      currentUserId={currentUserId}
      isMaster={isMaster}
      isModerator={isModerator}
      focusMessageId={focusMessageId}
      overlayContent={overlay}
      footerHint="Use a comunidade para trocar ideias sobre esta visao. Evite compartilhar dados sensiveis."
    />
  )
}

function formatLastInteractionLabel(iso: string | null | undefined): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null

  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMin / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMin < 1) return 'Ultima resposta ha pouco'
  if (diffHours < 24 && now.toDateString() === date.toDateString()) {
    const hh = String(date.getHours()).padStart(2, '0')
    const mm = String(date.getMinutes()).padStart(2, '0')
    return `Ultima resposta hoje as ${hh}h${mm}`
  }
  if (diffDays === 1) return 'Ultima resposta ha 1 dia'
  if (diffDays > 1) return `Ultima resposta ha ${diffDays} dias`
  return null
}
